import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForward, PersonAdd, Refresh } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { API_URL } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { getErrorMessage, parseApiErrorResponse } from '../utils/reviewHelpers';


interface AccountSlot {
  status: 'active' | 'pending';
  username?: string | null;
  email?: string | null;
  role: string;
}

interface SponsorSummary {
  company_id: string;
  company_code: string;
  display_name: string;
  logo_uri?: string | null;
  organization_status: 'configured' | 'missing';
  internal?: AccountSlot | null;
  external?: AccountSlot | null;
  extra_members: Array<{ identifier: string; role: string }>;
  extra_pending_invitations: number;
}

interface InternalFormState {
  mode: 'create' | 'replace' | 'password';
  username: string;
  password: string;
  confirmation: string;
}

interface ExternalFormState {
  mode: 'invite' | 'replace';
  email: string;
}

const CustomerDirectoryPanel = () => {
  const [sponsors, setSponsors] = useState<SponsorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadSucceeded, setLoadSucceeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [internalForms, setInternalForms] = useState<Record<string, InternalFormState>>({});
  const [externalForms, setExternalForms] = useState<Record<string, ExternalFormState>>({});

  const request = async (path: string, init?: RequestInit) => {
    const response = await authenticatedFetch(`${API_URL}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    });
    if (!response.ok) {
      throw new Error(await parseApiErrorResponse(response));
    }
    return response.status === 204 ? {} : response.json();
  };

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadSucceeded(false);
      const body = await request('/admin/accounts/sponsors');
      setSponsors(body.sponsors || []);
      setLoadSucceeded(true);
    } catch (loadError) {
      setSponsors([]);
      setError(getErrorMessage(loadError, 'Unable to load sponsor accounts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (key: string, action: () => Promise<unknown>, success: string) => {
    try {
      setBusy(key);
      setError(null);
      setMessage(null);
      await action();
      setMessage(success);
      await refresh();
      return true;
    } catch (actionError) {
      setError(getErrorMessage(actionError, 'Account operation failed'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const openInternalForm = (
    sponsor: SponsorSummary,
    mode: InternalFormState['mode']
  ) => {
    setInternalForms((current) => ({
      ...current,
      [sponsor.company_code]: {
        mode,
        username: mode === 'password' ? '' : sponsor.internal?.username || `${sponsor.company_code}-internal`,
        password: '',
        confirmation: '',
      },
    }));
  };

  const closeInternalForm = (companyCode: string) => {
    setInternalForms((current) => {
      const next = { ...current };
      delete next[companyCode];
      return next;
    });
  };

  const saveInternal = async (sponsor: SponsorSummary) => {
    const form = internalForms[sponsor.company_code];
    if (!form || form.password !== form.confirmation) {
      setError('The password confirmation does not match.');
      return;
    }
    const code = encodeURIComponent(sponsor.company_code);
    const path = `/admin/accounts/sponsors/${code}/internal`;
    const requestPath = form.mode === 'password' ? `${path}/password` : path;
    const method = form.mode === 'create' ? 'POST' : 'PUT';
    const body = form.mode === 'password'
      ? { password: form.password }
      : { username: form.username, password: form.password };
    const saved = await run(
      `${sponsor.company_code}-internal`,
      () => request(requestPath, { method, body: JSON.stringify(body) }),
      form.mode === 'password' ? 'Internal password rotated.' : 'Internal account saved.'
    );
    if (saved) closeInternalForm(sponsor.company_code);
  };

  const removeInternal = async (sponsor: SponsorSummary) => {
    if (!window.confirm(`Remove internal access for ${sponsor.display_name}?`)) return;
    const code = encodeURIComponent(sponsor.company_code);
    await run(
      `${sponsor.company_code}-internal`,
      () => request(`/admin/accounts/sponsors/${code}/internal`, { method: 'DELETE' }),
      'Internal account access removed.'
    );
  };

  const openExternalForm = (sponsor: SponsorSummary, mode: ExternalFormState['mode']) => {
    setExternalForms((current) => ({
      ...current,
      [sponsor.company_code]: {
        mode,
        email: mode === 'replace' ? sponsor.external?.email || '' : '',
      },
    }));
  };

  const closeExternalForm = (companyCode: string) => {
    setExternalForms((current) => {
      const next = { ...current };
      delete next[companyCode];
      return next;
    });
  };

  const saveExternal = async (sponsor: SponsorSummary) => {
    const form = externalForms[sponsor.company_code];
    if (!form) return;
    const code = encodeURIComponent(sponsor.company_code);
    const saved = await run(
      `${sponsor.company_code}-external`,
      async () => {
        if (form.mode === 'replace' && sponsor.external) {
          const removePath = sponsor.external.status === 'pending'
            ? `/admin/accounts/sponsors/${code}/external-invitations`
            : `/admin/accounts/sponsors/${code}/external`;
          await request(removePath, { method: 'DELETE' });
        }
        return request(`/admin/accounts/sponsors/${code}/external-invitations`, {
          method: 'POST',
          body: JSON.stringify({ email: form.email }),
        });
      },
      'Invitation created.'
    );
    if (saved) closeExternalForm(sponsor.company_code);
  };

  const resendExternal = async (sponsor: SponsorSummary) => {
    const code = encodeURIComponent(sponsor.company_code);
    await run(
      `${sponsor.company_code}-external`,
      () => request(`/admin/accounts/sponsors/${code}/external-invitations/resend`, {
        method: 'POST',
      }),
      'External sponsor invitation resent.'
    );
  };

  const removeExternal = async (sponsor: SponsorSummary) => {
    if (!window.confirm(`Remove external access for ${sponsor.display_name}?`)) return;
    const code = encodeURIComponent(sponsor.company_code);
    const path = sponsor.external?.status === 'pending'
      ? `/admin/accounts/sponsors/${code}/external-invitations`
      : `/admin/accounts/sponsors/${code}/external`;
    await run(
      `${sponsor.company_code}-external`,
      () => request(path, { method: 'DELETE' }),
      sponsor.external?.status === 'pending'
        ? 'Pending invitation revoked.'
        : 'External sponsor access removed.'
    );
  };

  if (loading) return <CircularProgress aria-label="Loading customer pages" />;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
        }}
      >
        <div>
          <Typography variant="h5" gutterBottom>Sponsor Pages and Accounts</Typography>
          <Typography color="text.secondary">
            Preview sponsor dashboards and manage the two viewer accounts assigned to each sponsor.
          </Typography>
        </div>
        <Button startIcon={<Refresh />} onClick={refresh} disabled={Boolean(busy)}>
          Refresh
        </Button>
      </div>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      <Grid container spacing={2}>
        {sponsors.map((sponsor) => {
          const internalForm = internalForms[sponsor.company_code];
          const externalForm = externalForms[sponsor.company_code];
          const sponsorBusy = busy?.startsWith(sponsor.company_code);
          return (
            <Grid item xs={12} lg={6} key={sponsor.company_id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="start" spacing={2}>
                    <div>
                      <Typography variant="overline" color="text.secondary">
                        {sponsor.company_code}
                      </Typography>
                      <Typography variant="h5">{sponsor.display_name}</Typography>
                    </div>
                    <Button
                      component={RouterLink}
                      to={`/dashboard/customers/${encodeURIComponent(sponsor.company_code)}`}
                      endIcon={<ArrowForward />}
                      variant="contained"
                    >
                      Open dashboard
                    </Button>
                  </Stack>

                  {sponsor.organization_status === 'missing' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Clerk organization missing. Creating the internal account will configure it.
                    </Alert>
                  )}
                  {(sponsor.extra_members.length > 0 || sponsor.extra_pending_invitations > 0) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Unexpected extra Clerk memberships or invitations were found. Resolve them in Clerk before adding accounts.
                    </Alert>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6">Internal review account</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Username and password account for NILUS internal review. The password is never displayed or stored here.
                  </Typography>
                  {sponsor.internal ? (
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Chip label={sponsor.internal.username || 'Username unavailable'} />
                      <Chip color="success" label="Active viewer" size="small" />
                      <Button size="small" onClick={() => openInternalForm(sponsor, 'replace')}>
                        Replace credentials
                      </Button>
                      <Button size="small" onClick={() => openInternalForm(sponsor, 'password')}>
                        Reset password
                      </Button>
                      <Button size="small" color="error" onClick={() => removeInternal(sponsor)}>
                        Remove
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<PersonAdd />}
                      onClick={() => openInternalForm(sponsor, 'create')}
                    >
                      Set up internal account
                    </Button>
                  )}
                  {internalForm && (
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      {internalForm.mode !== 'password' && (
                        <TextField
                          label="Username"
                          value={internalForm.username}
                          onChange={(event) => setInternalForms((current) => ({
                            ...current,
                            [sponsor.company_code]: {
                              ...internalForm,
                              username: event.target.value,
                            },
                          }))}
                          size="small"
                        />
                      )}
                      <TextField
                        label={internalForm.mode === 'password' ? 'New password' : 'Password'}
                        type="password"
                        autoComplete="new-password"
                        value={internalForm.password}
                        onChange={(event) => setInternalForms((current) => ({
                          ...current,
                          [sponsor.company_code]: {
                            ...internalForm,
                            password: event.target.value,
                          },
                        }))}
                        helperText="Use at least 12 characters."
                        size="small"
                      />
                      <TextField
                        label="Confirm password"
                        type="password"
                        autoComplete="new-password"
                        value={internalForm.confirmation}
                        onChange={(event) => setInternalForms((current) => ({
                          ...current,
                          [sponsor.company_code]: {
                            ...internalForm,
                            confirmation: event.target.value,
                          },
                        }))}
                        size="small"
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          disabled={sponsorBusy || !internalForm.password || !internalForm.confirmation}
                          onClick={() => saveInternal(sponsor)}
                        >
                          Save
                        </Button>
                        <Button onClick={() => closeInternalForm(sponsor.company_code)}>Cancel</Button>
                      </Stack>
                    </Stack>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6">External sponsor account</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Email invitation for the sponsor's viewer account.
                  </Typography>
                  {sponsor.external ? (
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Chip label={sponsor.external.email || 'Email unavailable'} />
                      <Chip
                        color={sponsor.external.status === 'active' ? 'success' : 'warning'}
                        label={sponsor.external.status === 'active' ? 'Active viewer' : 'Invitation pending'}
                        size="small"
                      />
                      {sponsor.external.status === 'pending' && (
                        <Button size="small" onClick={() => resendExternal(sponsor)}>Resend</Button>
                      )}
                      <Button size="small" onClick={() => openExternalForm(sponsor, 'replace')}>
                        Replace
                      </Button>
                      <Button size="small" color="error" onClick={() => removeExternal(sponsor)}>
                        {sponsor.external.status === 'pending' ? 'Revoke' : 'Remove'}
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<PersonAdd />}
                      disabled={sponsor.organization_status === 'missing'}
                      onClick={() => openExternalForm(sponsor, 'invite')}
                    >
                      Invite external viewer
                    </Button>
                  )}
                  {externalForm && (
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      <TextField
                        label="Sponsor email"
                        type="email"
                        autoComplete="email"
                        value={externalForm.email}
                        onChange={(event) => setExternalForms((current) => ({
                          ...current,
                          [sponsor.company_code]: {
                            ...externalForm,
                            email: event.target.value,
                          },
                        }))}
                        size="small"
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          disabled={sponsorBusy || !externalForm.email}
                          onClick={() => saveExternal(sponsor)}
                        >
                          Send invitation
                        </Button>
                        <Button onClick={() => closeExternalForm(sponsor.company_code)}>Cancel</Button>
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {loadSucceeded && !sponsors.length && (
        <Typography color="text.secondary">No active sponsors are configured.</Typography>
      )}
    </div>
  );
};

export default CustomerDirectoryPanel;
